import type {
  PaginatedResult,
  ProductBadge,
  ProductMediaReference,
  ProductOptionDefinition,
  ProductType,
  PublicCategorySummary,
  PublicProductDetail,
  PublicProductSummary,
} from '@emazy/shared-types';

export type CatalogSort = 'featured' | 'newest';

export type PublicCategoryListInput = { limit?: number; cursor?: string };
export type PublicProductListInput = {
  category?: string;
  productType?: ProductType;
  featured?: boolean;
  badge?: ProductBadge;
  sort?: CatalogSort;
  limit?: number;
  cursor?: string;
};
export type FeaturedProductListInput = Pick<PublicProductListInput, 'limit' | 'cursor'>;

export interface PublicCatalogService {
  listCategories(
    input?: PublicCategoryListInput,
    signal?: AbortSignal,
  ): Promise<PaginatedResult<PublicCategorySummary>>;
  getCategory(slug: string, signal?: AbortSignal): Promise<PublicCategorySummary>;
  listProducts(
    input?: PublicProductListInput,
    signal?: AbortSignal,
  ): Promise<PaginatedResult<PublicProductSummary>>;
  listCategoryProducts(
    categorySlug: string,
    input?: Omit<PublicProductListInput, 'category'>,
    signal?: AbortSignal,
  ): Promise<PaginatedResult<PublicProductSummary>>;
  getProduct(slug: string, signal?: AbortSignal): Promise<PublicProductDetail>;
  listFeaturedProducts(
    input?: FeaturedProductListInput,
    signal?: AbortSignal,
  ): Promise<PaginatedResult<PublicProductSummary>>;
}

export type CatalogErrorCode =
  | 'catalog-unavailable'
  | 'category-not-found'
  | 'product-not-found'
  | 'invalid-filter'
  | 'invalid-response'
  | 'network-error'
  | 'unknown';

export class CatalogError extends Error {
  constructor(
    readonly code: CatalogErrorCode,
    message: string,
    readonly status?: number,
    options?: ErrorOptions,
  ) {
    super(message, options);
    this.name = 'CatalogError';
  }
}

export type CategoryCardModel = {
  id: string;
  slug: string;
  title: string;
  shortTitle: string;
  description: string;
  route: string;
  icon: string;
  imageUrl?: string;
  imageAlt?: string;
};

export type ProductCardModel = {
  id: string;
  slug: string;
  category: string;
  name: string;
  shortDescription: string;
  startingPrice: number;
  startingPriceLabel: string;
  compareAtPriceLabel?: string;
  currency: 'USD';
  imageUrl: string;
  imageAlt: string;
  badges: ProductBadge[];
  featured: boolean;
  customizable: boolean;
  active: true;
};

export type ProductDetailViewModel = ProductCardModel & {
  description: string;
  media: Array<{ id: string; url: string; alt: string; width?: number; height?: number }>;
  options: ProductOptionDefinition[];
  customizationSummary: string[];
  seo: { title?: string; description?: string };
};

export type CatalogMediaConfig = { baseUrl?: string; placeholderUrl: string };
export type CatalogMediaInput = Pick<ProductMediaReference, 'objectKey' | 'altText'>;
